/**
 * TopicsIndex — real /topics landing page.
 *
 * Previously /topics fell into a generic profile resolver and rendered
 * "Could not load profile." — a soft 404 the Sept-2026 audit flagged as
 * a launch blocker.
 *
 * This page renders a static (fast, always-200) topic catalogue with 14
 * top-level topics linking through to their existing SocialPulse
 * per-topic hub routes. It's intentionally light — the deeper Topic hub
 * with live counts + subscriptions will land in Phase 2.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Users, Home as HomeIcon, HeartPulse, GraduationCap, Utensils, Shield,
  Leaf, Landmark, Briefcase, Camera, Newspaper, Plane, HandHeart, Globe2, ArrowRight,
} from "lucide-react";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

const TOPICS = [
  { slug: "community",    label: "Community",           icon: Users,         lead: "Neighbourhood conversations, meet-ups and local groups.",       to: "/community" },
  { slug: "civic",        label: "Civic Issues",        icon: Landmark,      lead: "Report and follow potholes, water, waste, safety, transit.",    to: "/civic" },
  { slug: "housing",      label: "Housing & Shelter",   icon: HomeIcon,      lead: "Rentals, homelessness support, and neighbourhood housing.",     to: "/topics/housing" },
  { slug: "health",       label: "Health & Wellbeing",  icon: HeartPulse,    lead: "Local clinics, mental-health resources, and health topics.",    to: "/topics/health" },
  { slug: "education",    label: "Education",           icon: GraduationCap, lead: "Schools, alumni groups, scholarships, and learning.",           to: "/topics/education" },
  { slug: "food",         label: "Food & Basics",       icon: Utensils,      lead: "Food banks, local restaurants, and community meals.",           to: "/topics/food" },
  { slug: "safety",       label: "Safety & Emergency",  icon: Shield,        lead: "Emergency services, disaster response, and safety alerts.",     to: "/topics/safety" },
  { slug: "environment",  label: "Environment",         icon: Leaf,          lead: "Climate, sustainability, parks, and green initiatives.",        to: "/topics/environment" },
  { slug: "business",     label: "Business & Work",     icon: Briefcase,     lead: "Local businesses, employment, and community commerce.",         to: "/topics/business" },
  { slug: "photography",  label: "Photography",         icon: Camera,        lead: "Local photo essays, event coverage, and creators.",             to: "/topics/photography" },
  { slug: "news",         label: "News",                icon: Newspaper,     lead: "Local and global news across the SocialPulse network.",         to: "/topics/news" },
  { slug: "travel",       label: "Travel",              icon: Plane,         lead: "Destinations, itineraries, and traveller stories.",             to: "/topics/travel" },
  { slug: "causes",       label: "Causes & Volunteering", icon: HandHeart,   lead: "Give time, resources, or money to verified causes.",            to: "/causes" },
  { slug: "world",        label: "World",               icon: Globe2,        lead: "Country and regional pulses across the network.",               to: "/network" },
];

export default function TopicsIndex() {
  useEffect(() => {
    document.title = "Explore Topics · SocialPulse";
    setMeta("description", "Browse SocialPulse topics — community, civic issues, housing, health, education, food, safety, environment, business, photography, news, travel, causes and more.");
    setLink("canonical", "https://socialpulse.com/topics");
    setMeta("og:title", "Explore Topics · SocialPulse", "property");
    setMeta("og:url", "https://socialpulse.com/topics", "property");
    setMeta("og:type", "website", "property");
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="sp-topics-page">
      <SocialPulseHeader />
      <main className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 py-10 sm:py-14">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[.22em] text-violet-700">Explore</p>
          <h1
            className="mt-3 font-serif font-bold tracking-tight text-slate-900 leading-tight text-3xl sm:text-4xl lg:text-5xl"
            data-testid="sp-topics-h1"
          >
            Every topic. <span className="text-emerald-700">One network.</span>
          </h1>
          <p className="mt-4 sm:mt-5 text-slate-600 text-base sm:text-lg leading-relaxed">
            SocialPulse connects local voices around the topics that matter —
            follow one, follow many, or drop into whichever specialist site
            covers the topic best.
          </p>
        </div>

        <div className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {TOPICS.map((t) => (
            <TopicCard key={t.slug} topic={t} />
          ))}
        </div>

        <div className="mt-14 rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-6 flex flex-wrap gap-4 items-center justify-between">
          <p className="text-sm text-slate-600">
            Prefer to browse by place first? SocialPulse indexes every topic against a geographic hierarchy.
          </p>
          <Link
            to="/places"
            data-testid="sp-topics-places-cta"
            className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 text-sm transition-colors"
          >
            Explore places <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
      <SocialPulseFooter />
    </div>
  );
}

function TopicCard({ topic }) {
  const Icon = topic.icon;
  return (
    <Link
      to={topic.to}
      data-testid={`sp-topic-${topic.slug}`}
      className="group flex items-start gap-4 rounded-2xl ring-1 ring-slate-200 bg-white p-5 hover:ring-blue-300 hover:shadow-md transition-all"
    >
      <span className="w-11 h-11 grid place-items-center rounded-full bg-blue-50 text-blue-700 shrink-0">
        <Icon className="w-5 h-5" strokeWidth={2.2} />
      </span>
      <div className="flex-1 min-w-0">
        <h3 className="font-serif font-bold text-slate-900 text-base leading-tight">
          {topic.label}
        </h3>
        <p className="mt-1 text-[13px] text-slate-600 leading-snug">
          {topic.lead}
        </p>
      </div>
      <ArrowRight className="w-4 h-4 text-blue-600 shrink-0 mt-1 group-hover:translate-x-1 transition-transform" />
    </Link>
  );
}

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
